import { create } from 'zustand'
import type { TranscriptMessage, ProfileField, ProfileFields } from '../types'
import type { ConflictInfo, AlternativeSlot } from '../components/ConflictAlertModal'

interface CallState {
  // Transcript
  transcript: TranscriptMessage[]
  addTranscript: (message: Omit<TranscriptMessage, 'id'>) => void
  clearTranscript: () => void
  
  // Profile fields
  fields: ProfileFields
  updateField: (fieldName: keyof ProfileFields, data: ProfileField) => void
  clearFields: () => void
  
  // Call metadata
  callId: string | null
  callDuration: number
  callStartTime: number | null
  overallConfidence: number
  isBookingReady: boolean
  isCallActive: boolean
  
  // Service duration for availability checks (calculated from selected services)
  serviceDuration: number
  setServiceDuration: (duration: number) => void
  
  // AI Listening pause - stops AI from updating form fields (transcript still flows)
  isAIPaused: boolean
  setAIPaused: (paused: boolean) => void
  toggleAIPaused: () => void
  
  // Intent detection - what does the customer want?
  detectedIntent: 'booking' | 'cancellation' | 'inquiry' | 'reschedule' | null
  setDetectedIntent: (intent: 'booking' | 'cancellation' | 'inquiry' | 'reschedule' | null) => void
  
  // Cancellation flow modal
  cancellationModal: {
    isOpen: boolean
    customerPhone: string | null
    customerName: string | null
  }
  showCancellationModal: () => void
  hideCancellationModal: () => void
  
  // Session management (Phase 2)
  currentSessionId: string | null
  isLoadingSession: boolean
  
  // Conflict detection (Phase 2)
  conflictModal: {
    isOpen: boolean
    conflict: ConflictInfo | null
    suggestions: AlternativeSlot[]
  }
  showConflictModal: (data: { conflict: ConflictInfo; suggestions: AlternativeSlot[] }) => void
  hideConflictModal: () => void
  hasCheckedAvailability: boolean
  
  // Availability state
  slotAvailability: {
    isChecking: boolean
    isAvailable: boolean | null
    conflict: ConflictInfo | null
    suggestions: AlternativeSlot[]
  }
  checkSlotAvailability: () => Promise<void>
  clearAvailability: () => void
  
  // Actions
  startCall: () => void
  endCall: () => void
  checkBookingReady: () => void
  reset: () => void
  loadSession: (sessionId: string) => Promise<void>
  createNewSession: () => void
}

const initialFields: ProfileFields = {
  firstName: null,
  lastName: null,
  preferredName: null,
  phone: null,
  email: null,
  service: null,
  date: null,
  time: null,
  notes: null,
  requestedStylist: null
}

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

export const useCallStore = create<CallState>((set, get) => {
  let durationInterval: ReturnType<typeof setInterval> | null = null

  return {
    transcript: [],
    fields: { ...initialFields },
    callId: null,
    callDuration: 0,
    callStartTime: null,
    overallConfidence: 0,
    isBookingReady: false,
    isCallActive: false,
    
    // Service duration (default 60 mins, updated when services selected)
    serviceDuration: 60,
    setServiceDuration: (duration: number) => {
      set({ serviceDuration: duration })
      // Re-check availability with new duration - this is critical!
      // A 30 min service might fit where a 90 min service won't
      setTimeout(() => {
        get().checkSlotAvailability()
      }, 50)
    },
    
    // AI listening pause
    isAIPaused: false,
    setAIPaused: (paused: boolean) => set({ isAIPaused: paused }),
    toggleAIPaused: () => set(state => ({ isAIPaused: !state.isAIPaused })),
    
    // Intent detection
    detectedIntent: null,
    
    // Cancellation flow modal
    cancellationModal: {
      isOpen: false,
      customerPhone: null,
      customerName: null
    },
    
    // Session management (Phase 2)
    currentSessionId: null,
    isLoadingSession: false,
    
    // Conflict detection (Phase 2)
    conflictModal: {
      isOpen: false,
      conflict: null,
      suggestions: []
    },
    hasCheckedAvailability: false,
    
    // Availability state
    slotAvailability: {
      isChecking: false,
      isAvailable: null,
      conflict: null,
      suggestions: []
    },

    addTranscript: (message) => {
      const id = crypto.randomUUID()
      set((state) => ({
        transcript: [...state.transcript, { ...message, id }]
      }))
    },

    clearTranscript: () => {
      set({ transcript: [] })
    },

    updateField: (fieldName, data) => {
      set((state) => ({
        fields: {
          ...state.fields,
          [fieldName]: data
        }
      }))
      get().checkBookingReady()
      
      // Auto-check availability when date, time, stylist, or SERVICE is updated
      // Service matters because duration affects whether slot is available
      if (fieldName === 'date' || fieldName === 'time' || fieldName === 'requestedStylist' || fieldName === 'service') {
        // Small delay to allow fields and duration to be set
        setTimeout(() => {
          get().checkSlotAvailability()
        }, 150)
      }
    },

    clearFields: () => {
      set({ fields: { ...initialFields } })
    },

    startCall: () => {
      const callId = crypto.randomUUID()
      const callStartTime = Date.now()
      
      set({ 
        callId, 
        callStartTime, 
        callDuration: 0, 
        isCallActive: true 
      })

      // Update duration every second
      durationInterval = setInterval(() => {
        const { callStartTime: start } = get()
        if (start) {
          set({ callDuration: Math.floor((Date.now() - start) / 1000) })
        }
      }, 1000)
    },

    endCall: () => {
      if (durationInterval) {
        clearInterval(durationInterval)
        durationInterval = null
      }
      set({ 
        callStartTime: null, 
        isCallActive: false 
      })
    },

    checkBookingReady: () => {
      const { fields } = get()
      
      // Essential fields for booking
      // Confidence thresholds tuned for salon use case:
      // - 70%+ = booking ready (stylist can trust and book)
      // - <70% = needs verbal confirmation before booking
      const essentialFields: (keyof ProfileFields)[] = ['firstName', 'phone', 'service', 'date', 'time']
      
      const allReady = essentialFields.every((fieldName) => {
        const field = fields[fieldName]
        return field && field.confidence >= 70 && field.status !== 'warning'
      })

      // Calculate overall confidence from filled essential fields
      const filledFields = essentialFields
        .map((f) => fields[f])
        .filter((f): f is ProfileField => f !== null)
      
      const overallConfidence = filledFields.length > 0
        ? Math.round(filledFields.reduce((sum, f) => sum + f.confidence, 0) / filledFields.length)
        : 0

      set({ 
        isBookingReady: allReady, 
        overallConfidence 
      })
    },

    reset: () => {
      if (durationInterval) {
        clearInterval(durationInterval)
        durationInterval = null
      }
      set({
        transcript: [],
        fields: { ...initialFields },
        callId: null,
        callDuration: 0,
        callStartTime: null,
        overallConfidence: 0,
        isBookingReady: false,
        isCallActive: false,
        hasCheckedAvailability: false,
        currentSessionId: null,
        isLoadingSession: false,
        serviceDuration: 60, // Reset to default
        conflictModal: {
          isOpen: false,
          conflict: null,
          suggestions: []
        }
      })
    },
    
    // Session management (Phase 2)
    loadSession: async (sessionId: string) => {
      set({ isLoadingSession: true })
      
      try {
        const response = await fetch(`${API_URL}/api/calls/session/${sessionId}`)
        const data = await response.json()
        
        if (data.error) {
          console.error('Failed to load session:', data.error)
          set({ isLoadingSession: false })
          return
        }
        
        // Reconstruct fields from entities first
        const reconstructedFields = { ...initialFields }
        if (data.entities && Array.isArray(data.entities)) {
          for (const entity of data.entities) {
            const fieldName = entity.field_name as keyof ProfileFields
            if (fieldName in reconstructedFields) {
              reconstructedFields[fieldName] = {
                value: entity.value,
                confidence: entity.confidence,
                status: entity.status || 'mentioned',
                updatedAt: entity.timestamp
              }
            }
          }
        }
        
        // If we have customer data, populate from that (with high confidence since it's saved)
        // Note: check ?.value since initialFields has null objects
        if (data.customer) {
          const c = data.customer
          if (c.first_name && !reconstructedFields.firstName?.value) {
            reconstructedFields.firstName = {
              value: c.first_name,
              confidence: 100,
              status: 'confirmed',
              updatedAt: c.updated_at
            }
          }
          if (c.last_name && !reconstructedFields.lastName?.value) {
            reconstructedFields.lastName = {
              value: c.last_name,
              confidence: 100,
              status: 'confirmed',
              updatedAt: c.updated_at
            }
          }
          if (c.phone && !reconstructedFields.phone?.value) {
            reconstructedFields.phone = {
              value: c.phone,
              confidence: 100,
              status: 'confirmed',
              updatedAt: c.updated_at
            }
          }
          if (c.email && !reconstructedFields.email?.value) {
            reconstructedFields.email = {
              value: c.email,
              confidence: 100,
              status: 'confirmed',
              updatedAt: c.updated_at
            }
          }
          if (c.notes && !reconstructedFields.notes?.value) {
            reconstructedFields.notes = {
              value: c.notes,
              confidence: 100,
              status: 'confirmed',
              updatedAt: c.updated_at
            }
          }
        }
        
        // If we have appointment data, populate from that
        if (data.appointment) {
          const a = data.appointment
          if (a.service && !reconstructedFields.service?.value) {
            reconstructedFields.service = {
              value: a.service,
              confidence: 100,
              status: 'confirmed',
              updatedAt: a.created_at
            }
          }
          if (a.date && !reconstructedFields.date?.value) {
            reconstructedFields.date = {
              value: a.date,
              confidence: 100,
              status: 'confirmed',
              updatedAt: a.created_at
            }
          }
          if (a.time && !reconstructedFields.time?.value) {
            reconstructedFields.time = {
              value: a.time,
              confidence: 100,
              status: 'confirmed',
              updatedAt: a.created_at
            }
          }
          if (a.stylist_name && !reconstructedFields.requestedStylist?.value) {
            reconstructedFields.requestedStylist = {
              value: a.stylist_name,
              confidence: 100,
              status: 'confirmed',
              updatedAt: a.created_at
            }
          }
        }
        
        // Calculate booking readiness
        const essentialFields: (keyof ProfileFields)[] = ['firstName', 'phone', 'service', 'date', 'time']
        const filledFields = essentialFields
          .map((f) => reconstructedFields[f])
          .filter((f): f is ProfileField => f !== null && f.value !== null && f.value !== '')
        
        const overallConfidence = filledFields.length > 0
          ? Math.round(filledFields.reduce((sum, f) => sum + f.confidence, 0) / filledFields.length)
          : 0
        
        const isBookingReady = essentialFields.every((fieldName) => {
          const field = reconstructedFields[fieldName]
          return field && field.confidence >= 70 && field.status !== 'warning'
        })
        
        // Ensure transcript messages have IDs
        const transcriptWithIds = (data.transcript || []).map((msg: any, index: number) => ({
          ...msg,
          id: msg.id || `loaded-${sessionId}-${index}`
        }))
        
        set({
          currentSessionId: sessionId,
          callId: data.call.id,
          transcript: transcriptWithIds,
          fields: reconstructedFields,
          callStartTime: data.call.startedAt,
          callDuration: data.call.duration || 0,
          overallConfidence,
          isBookingReady,
          isCallActive: false,
          isLoadingSession: false
        })
      } catch (error) {
        console.error('Failed to load session:', error)
        set({ isLoadingSession: false })
      }
    },
    
    createNewSession: () => {
      const sessionId = crypto.randomUUID()
      
      set({
        currentSessionId: sessionId,
        transcript: [],
        fields: { ...initialFields },
        callStartTime: Date.now(),
        callDuration: 0,
        hasCheckedAvailability: false
      })
    },
    
    // Intent detection
    setDetectedIntent: (intent) => {
      const prevIntent = get().detectedIntent
      set({ detectedIntent: intent })
      
      // Auto-show cancellation modal when intent changes to cancellation
      if (intent === 'cancellation' && prevIntent !== 'cancellation') {
        const { fields } = get()
        set({
          cancellationModal: {
            isOpen: true,
            customerPhone: fields.phone?.value || null,
            customerName: fields.firstName?.value 
              ? `${fields.firstName.value} ${fields.lastName?.value || ''}`.trim()
              : null
          }
        })
      }
    },
    
    // Cancellation flow modal
    showCancellationModal: () => {
      const { fields } = get()
      set({
        cancellationModal: {
          isOpen: true,
          customerPhone: fields.phone?.value || null,
          customerName: fields.firstName?.value 
            ? `${fields.firstName.value} ${fields.lastName?.value || ''}`.trim()
            : null
        }
      })
    },
    
    hideCancellationModal: () => {
      set({
        cancellationModal: {
          isOpen: false,
          customerPhone: null,
          customerName: null
        }
      })
    },
    
    // Conflict detection (Phase 2)
    showConflictModal: (data) => {
      console.log('[showConflictModal] Called with:', {
        conflict: data.conflict,
        suggestionsCount: data.suggestions?.length,
        suggestions: data.suggestions
      })
      set({
        conflictModal: {
          isOpen: true,
          conflict: data.conflict,
          suggestions: data.suggestions || []
        }
      })
    },
    
    hideConflictModal: () => {
      set({
        conflictModal: {
          isOpen: false,
          conflict: null,
          suggestions: []
        }
      })
    },
    
    // Check slot availability
    checkSlotAvailability: async () => {
      const { fields } = get()
      const dateField = fields.date
      const timeField = fields.time
      const stylistField = fields.requestedStylist
      
      // Only check if both date and time have values
      if (!dateField?.value || !timeField?.value) {
        set({
          slotAvailability: {
            isChecking: false,
            isAvailable: null,
            conflict: null,
            suggestions: []
          }
        })
        return
      }
      
      // Get service duration BEFORE we start
      const { serviceDuration: currentDuration } = get()
      console.log(`[checkSlotAvailability] Starting check:`)
      console.log(`  - date: "${dateField.value}"`)
      console.log(`  - time: "${timeField.value}"`)  
      console.log(`  - stylist: "${stylistField?.value || 'any'}"`)
      console.log(`  - duration: ${currentDuration} mins`)
      
      set({
        slotAvailability: {
          ...get().slotAvailability,
          isChecking: true
        }
      })
      
      try {
        // Get service duration from store (calculated from selected services)
        const { serviceDuration } = get()
        
        // Build request body - ALWAYS include stylist if selected
        // Duration comes from selected services (not hardcoded 60)
        const requestBody: Record<string, any> = {
          date: dateField.value,
          time: timeField.value,
          duration: serviceDuration || 60,
          stylistName: stylistField?.value || undefined
        }
        
        console.log('[checkSlotAvailability] Sending request:', JSON.stringify(requestBody), `duration=${serviceDuration}`)
        
        const response = await fetch(`${API_URL}/api/appointments/check-availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        })
        
        const result = await response.json()
        
        console.log(`[checkSlotAvailability] Result:`, result)
        
        // Log debug info if present
        if (result.debug) {
          console.log(`[checkSlotAvailability] Debug - Original: ${result.debug.originalDate} ${result.debug.originalTime} -> Normalized: ${result.debug.normalizedDate} ${result.debug.normalizedTime}, Appointments found: ${result.debug.appointmentsFound}`)
        }
        
        set({
          slotAvailability: {
            isChecking: false,
            isAvailable: result.available,
            conflict: result.available ? null : result.conflicts?.[0] || null,
            suggestions: result.available ? [] : result.suggestions || []
          }
        })
      } catch (error) {
        console.error('Failed to check availability:', error)
        set({
          slotAvailability: {
            isChecking: false,
            isAvailable: null,
            conflict: null,
            suggestions: []
          }
        })
      }
    },
    
    clearAvailability: () => {
      set({
        slotAvailability: {
          isChecking: false,
          isAvailable: null,
          conflict: null,
          suggestions: []
        }
      })
    }
  }
})

// Export helper to get missing fields for button text
export const getMissingFields = (fields: ProfileFields): string[] => {
  const essentialFields: (keyof ProfileFields)[] = ['firstName', 'phone', 'service', 'date', 'time']
  const fieldLabels: Record<string, string> = {
    firstName: 'Name',
    phone: 'Phone',
    service: 'Service',
    date: 'Date',
    time: 'Time'
  }
  
  return essentialFields
    .filter((f) => {
      const field = fields[f]
      return !field || field.confidence < 70 || field.status === 'warning'
    })
    .map((f) => fieldLabels[f] || f)
}
